const STORAGE_PREFIX = 'pf-preview-page-bootstrap:'

export type BootstrapMissingPreviewPageResult =
  | 'skipped-not-preview'
  | 'skipped-known-error-route'
  | 'exists'
  | 'already-attempted'
  | 'created'
  | 'failed'

type BootstrapMissingPreviewPageOptions = {
  pathname: string
  isPreviewFrame: boolean
  refresh: () => Promise<void>
  reload?: () => void
  fetchImpl?: typeof fetch
  storage?: Pick<Storage, 'getItem' | 'setItem'>
  pollIntervalMs?: number
  pollTimeoutMs?: number
  sleep?: (ms: number) => Promise<void>
}

const KNOWN_ERROR_ROUTES = new Set(['/error', '/404', '/404.html', '/dev-404-page'])

const defaultSleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms))

/**
 * Gatsby develop page-data URL for a pathname (`/blog/foo` → `/page-data/blog/foo/page-data.json`).
 */
export const getPageDataUrl = (pathname: string): string => {
  const normalized = pathname.replace(/\/+$/, '') || '/'
  const pathPart = normalized === '/' ? '/index' : normalized
  return `/page-data${pathPart}/page-data.json`
}

export const pageDataExists = async (pathname: string, fetchImpl: typeof fetch = fetch): Promise<boolean> => {
  try {
    const response = await fetchImpl(getPageDataUrl(pathname), {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
    })
    return response.ok
  } catch {
    return false
  }
}

/**
 * When Live Preview opens a slug Gatsby has not `createPage`d yet (new draft Page), develop shows
 * its built-in 404. Save-and-refresh only reloads the iframe — it does not re-source Contentful.
 *
 * If we're in the Contentful iframe and page-data is missing: POST `/__contentful-preview/refresh`,
 * poll until page-data appears, then reload so the new route mounts.
 *
 * One attempt per pathname per tab (`sessionStorage`) to avoid refresh loops when the entry still
 * cannot produce a page (missing slug, wrong content type, etc.).
 */
export const bootstrapMissingPreviewPage = async ({
  pathname,
  isPreviewFrame,
  refresh,
  reload = () => window.location.reload(),
  fetchImpl = fetch,
  storage = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
  pollIntervalMs = 1000,
  pollTimeoutMs = 120000,
  sleep = defaultSleep,
}: BootstrapMissingPreviewPageOptions): Promise<BootstrapMissingPreviewPageResult> => {
  if (!isPreviewFrame) return 'skipped-not-preview'

  const normalizedPath = pathname.replace(/\/+$/, '') || '/'
  if (KNOWN_ERROR_ROUTES.has(normalizedPath)) return 'skipped-known-error-route'

  if (await pageDataExists(normalizedPath, fetchImpl)) return 'exists'

  const storageKey = `${STORAGE_PREFIX}${normalizedPath}`
  if (storage?.getItem(storageKey)) return 'already-attempted'

  storage?.setItem(storageKey, String(Date.now()))

  try {
    await refresh()
  } catch (error) {
    console.error('[contentful-preview-bootstrap] refresh failed', error)
    return 'failed'
  }

  const deadline = Date.now() + pollTimeoutMs
  while (Date.now() < deadline) {
    if (await pageDataExists(normalizedPath, fetchImpl)) {
      reload()
      return 'created'
    }
    await sleep(pollIntervalMs)
  }

  console.error(`[contentful-preview-bootstrap] timed out waiting for page-data at ${getPageDataUrl(normalizedPath)}`)
  return 'failed'
}
